import React, { FC } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useIntl } from 'react-intl';
import clipboardCopy from 'clipboard-copy';
import toastr from 'toastr';

import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface LinkRendererProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  asBlank?: boolean;
}

const LinkRenderer: FC<LinkRendererProps> = ({ href, children, asBlank, ...props }) => {
  const isTermsOrPrivacy = href && ['#terms-of-service','#privacy-policy'].includes(href);
  href = isTermsOrPrivacy && asBlank ? href?.replace("#", '') : href;
  return <a href={href} target={href && isTermsOrPrivacy && !asBlank ? "_self" : "_blank"} rel="noopener noreferrer" {...props}>
    {children + (href?.includes('quran.com') ? ' ' : '')}
  </a>
};

const Table: FC<React.TableHTMLAttributes<HTMLTableElement>> = ({ children, ...props }) => (
  <table className="table table-bordered g-2 border-gray-400" {...props}>
  {children}
  </table>
);

const regex = /@@color\((.*?)\)(.*?)@@/g;


const renderUserMessage = (message: string): JSX.Element[] => {
  return message.split("\n").map((line, index) => (
    <div key={index}>{line}</div>
  ))
}

const escapeCurrencyDollars = (text) => {
  return text.replace(/(^|\s)\$(\d)/g, '$1&#36;$2');
};

function sanitizeButKeepLinks(html: string): string {
  return html
    // Remove everything except <a>...</a>
    .replace(/<(?!\/?a(?=>|\s.*>))[^>]+>/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export const convertIslamicTagsIntoMarkdownLinks = (text) => {
  return text.replace(
    /<quran_start>([\s\S]*?)<quran_end>/g,
    (_, quran) => {
      const clean = sanitizeButKeepLinks(quran);
      return `<span class="quran">${clean}</span>`;
    }
  )
  .replace(
    /<warning_start>([\s\S]*?)<warning_end>/g,
    (_, warning) => {
      const clean = warning.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
      return `<span class="quran-warning">${clean}</span>`;
    }
  );
}

export const removeIslamicTAGs = (text) => {
  return text.replace(/<\/?(quran|warning)_(start|end)>/g, '');
}

const replaceDollarSignsAndEscapeSequences = (text) => {
  let updatedText = escapeCurrencyDollars(text);
  const inlineLatexRegex = /\\\((.+?)\\\)/gs;
  const blockLatexRegex = /\\\[(.+?)\\\]/gs;
  updatedText = updatedText
    .replace(inlineLatexRegex, (match, p1) => `$${p1}$`) // Replace inline LaTeX
    .replace(blockLatexRegex, (match, p1) => `$$${p1}$$`); // Replace block LaTeX
  
  updatedText = updatedText.replace(/(.)(\$\$)/g, '$1\n$2');  
  updatedText = updatedText.replace(/(\$\$)(.)/g, '$1\n$2');  
  return updatedText;
};

function embraceBoxedWithDollars(input: string): string {
  return input.replace(/(?<!\$)\s*\\boxed\{.*?\}\s*(?!\$)/g, (match) => `$$${match.trim()}$$`);
}

const MarkdownWithLinks = ({ children, className='', asBlank=false }) => {
  const intl = useIntl();
  const selectedLang = intl.locale || 'en';
  const handleCopy = (code) => {
    clipboardCopy(code);
	  toastr.info(intl.formatMessage({id: `chat.message.copied`}))
  };
  return (
    <ReactMarkdown
      rehypePlugins={[rehypeRaw, rehypeKatex]}
      remarkPlugins={[remarkMath, remarkGfm]}
      className={className}
      components={{
        a: (props) => <LinkRenderer {...props} asBlank={asBlank}/>,
        table: Table,
        code({ node, className, children, ...props }) {
          const match = /language-(\w+)/.exec(className || '');
          let logoBlock = '';
          // Extract the logo block separately from the children
          const cleanedChildren = String(children).replace(
            /(<span id="logo"[^>]*>.*?<\/span>)/gs, // Capture the <span id="logo">
            (match, logo) => {
              logoBlock = logo; // Store the logo block
              return ''; // Remove the logo block from the code children
            }
          );
          return match ? (
            <div className='position-relative' style={{direction: 'ltr'}}>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    {intl.formatMessage({ id: "chat.message.copy" })}
                  </Tooltip>
                }
              >
                <span
                  className={`cursor-pointer py-1 position-absolute top-0 ${selectedLang === 'en' ? 'end-0 me-4' : 'start-0 ms-4' } mt-2 z-index-1`}
                  onClick={()=>handleCopy(cleanedChildren)}
                >
                  <i className="fa-solid fa-copy fs-7 me-1"></i>
                </span>
              </OverlayTrigger>
              <SyntaxHighlighter
                language={match[1]}
                PreTag="div"
                style={materialDark}
                className="rounded"
              > 
                {cleanedChildren.replace(/\n$/, '')}
              </SyntaxHighlighter>
              {logoBlock && (
                <div
                  className="d-flex justify-content-start h-25px"
                  dangerouslySetInnerHTML={{ __html: logoBlock }}
                />
              )}
              </div>
          ) : (
            <code className={className} style={{direction: 'ltr'}} {...props}>
              {children}
              {logoBlock && (
                <div
                  className="d-flex justify-content-start h-25px"
                  dangerouslySetInnerHTML={{ __html: logoBlock }}
                />
              )}
            </code>
          );
        },
      }}
    >
      {embraceBoxedWithDollars(replaceDollarSignsAndEscapeSequences(children ? children.replace(regex, (_, color, text) => {
        return `<span class="bg-${color} text-light-${color}">${color =='danger' ? '<del>' : '' }${text}${color =='danger' ? '</del>' : '' }</span>`;
      }) : children))}
    </ReactMarkdown>
  );
};

export {MarkdownWithLinks, renderUserMessage, LinkRenderer};